import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import HomeView from '@/views/HomeView.vue'
import AdminDashboardView from '@/views/admin/AdminDashboardView.vue'
import AuthDebug from '../components/AuthDebug.vue'

const routes = [
  {
    path: '/',
    name: 'home',
    component: HomeView
  },
  {
    path: '/books',
    name: 'books',
    component: () => import('@/views/books/BookListView.vue')
  },
  {
    path: '/books/:id',
    name: 'book-detail',
    component: () => import('@/views/books/BookDetailView.vue')
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { guestOnly: true }
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/auth/RegisterView.vue'),
    meta: { guestOnly: true }
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('@/views/user/ProfileView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/bookmarks',
    name: 'bookmarks',
    component: () => import('@/views/user/BookmarksView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/subscription',
    name: 'subscription',
    component: () => import('@/views/subscription/SubscriptionView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/read/:id',
    name: 'secure-reader',
    component: () => import('@/views/reader/SecureReaderView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/reader/:bookId',
    name: 'reader',
    component: () => import('@/views/reader/SecureReaderView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/test-reader',
    name: 'test-reader',
    component: () => import('@/components/TestImageReader.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/auth-debug',
    name: 'auth-debug',
    component: AuthDebug
  },
  {
    path: '/author',
    name: 'AuthorDashboard',
    component: () => import('@/views/author/AuthorDashboard.vue'),
    meta: { requiresAuth: true, role: 'author' }
  },
  {
    path: '/editor',
    name: 'EditorDashboard',
    component: () => import('@/views/editor/EditorDashboard.vue'),
    meta: { requiresAuth: true, role: 'editor' }
  },

  {
    path: '/admin',
    name: 'admin',
    component: AdminDashboardView,
    meta: { 
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue')
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior() {
    // Always scroll to top
    return { top: 0 }
  }
})

// Navigation guards
router.beforeEach((to, from, next) => {
  const authStore = useAuthStore()
  
  // Check if route requires authentication
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!authStore.isAuthenticated) {
      next({ name: 'login' })
      return
    }
    
    // Check if route requires admin privileges
    if (to.matched.some(record => record.meta.requiresAdmin)) {
      if (!authStore.isAdmin) {
        next({ name: 'home' })
        return
      }
    }
    
    // Check if route requires specific role
    if (to.meta.role) {
      const userRole = authStore.user?.role
      if (userRole !== to.meta.role) {
        next({ name: 'home' })
        return
      }
    }
  }
  
  // Check if route is for guests only
  if (to.matched.some(record => record.meta.guestOnly)) {
    if (authStore.isAuthenticated) {
      next({ name: 'home' })
      return
    }
  }
  
  next()
})

export default router